import _ from 'lodash';
import React, {FunctionComponent, useEffect, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {Modal} from '@material-ui/core';
import {Checkbox} from '@treez-ui/core/forms';
import {CloseIcon, StoreIcon} from '@treez-ui/core/icons';
import {ProductInCatalog} from '@treez/commons/sharedTypings/product';
import {SnackbarType} from '@treez-ui/core/feedback/modals';
import {OrganizationState} from '~/redux/reducers/OrganizationReducer';
import {GlobalApplicationState} from '~/redux/reducers/RootReducer';
import {selectOrganizationState} from '~/redux/selectors/organizationSelectors';
import {getCatalogsThatCarryProduct} from '~/utils/ProductAPI';
import {onBackdropClickWrap} from '~/lib/backdropClickWrap';
import {clearSnackbar, showSnackbar} from '~/redux/actions/SnackbarActions';

interface Props {
    addProduct: Function;
    closeModal: Function;
    open: boolean;
    productId: number;
}

// This is a store selector, as that's what the user sees, even though
// we're actually manipulating catalogs
const StoreSelector: FunctionComponent<Props> = (props: Props) => {
    const {addProduct, closeModal, open, productId} = props;

    const [catalogsToCarryProduct, setCatalogsToCarryProduct] = useState<number[]>([]);
    const [catalogsWithoutProduct, setCatalogsWithoutProduct] = useState<number[]>([]);
    const [catalogsWithProduct, setCatalogsWithProduct] = useState<number[]>();
    const [needsRefresh, setNeedsRefresh] = useState<boolean>(true);

    const organizationState = useSelector<GlobalApplicationState, OrganizationState>(
        selectOrganizationState
    );

    const {stores, organizationId} = organizationState;
    const dispatch = useDispatch();

    useEffect(() => {
        if (needsRefresh) {
            getCatalogsThatCarryProduct(organizationId, productId)
                .then((response) => {
                    const catalogIds = _.map(response.catalogs, 'id');
                    setCatalogsWithProduct(catalogIds);
                    setCatalogsToCarryProduct([]);
                    setCatalogsWithoutProduct([]);
                    setNeedsRefresh(false);
                })
                .catch((error) => console.error(error));
        }
    }, [needsRefresh]);

    const handleCatalogSelection = (catalogId: number, include: boolean) => {
        if (include) {
            if (!catalogHasProduct(catalogId))
                setCatalogsToCarryProduct([...catalogsToCarryProduct, catalogId]);

            if (_.includes(catalogsWithoutProduct, catalogId)) {
                setCatalogsWithoutProduct(_.without(catalogsWithoutProduct, catalogId));
            }
        } else if (include === false) {
            if (catalogHasProduct(catalogId)) {
                setCatalogsWithoutProduct(_.uniq([...catalogsWithoutProduct, catalogId]));
            } else {
                setCatalogsToCarryProduct(_.without(catalogsToCarryProduct, catalogId));
            }
        }
    };

    const catalogHasProduct = (catalogId: number) => {
        return _.includes(catalogsWithProduct, catalogId);
    };

    const catalogIsSelected = (catalogId: number) => {
        return _.includes(catalogsToCarryProduct, catalogId);
    };

    return (
        <Modal
            open={open}
            onClose={onBackdropClickWrap(() => {
                closeModal();
            })}
        >
            <div className="add-to-stores-modal">
                <div className="add-to-stores-header">
                    <StoreIcon />
                    <div className="add-to-stores-headline">Add Product to Stores</div>
                    <button onClick={() => closeModal()}>
                        <CloseIcon />
                    </button>
                </div>
                <div className="store-list">
                    {_.map(stores, (store) => {
                        const {catalogId, name} = store;
                        if (catalogId == null) {
                            dispatch(
                                showSnackbar({
                                    onClose: () => dispatch(clearSnackbar()),
                                    type: SnackbarType.WARNING,
                                    content: `Store catalog is missing for ${store.name}, please contact support@treez.io`,
                                })
                            );
                            console.error(`Catalog for store ${name} could not be loaded.`);
                            return;
                        }

                        return (
                            <button className="store-select-button" key={`store-${store.id}`}>
                                <Checkbox
                                    disabled={catalogHasProduct(catalogId)}
                                    checked={
                                        catalogHasProduct(catalogId) || catalogIsSelected(catalogId)
                                    }
                                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                        const isChecked = e.target.checked;
                                        handleCatalogSelection(catalogId, isChecked);
                                    }}
                                />
                                <div className="store-select-store-name">{name}</div>
                            </button>
                        );
                    })}
                </div>
                <button
                    className="add-to-stores-modal-button"
                    onClick={() => {
                        const product: Partial<ProductInCatalog> = {
                            id: productId,
                        };
                        addProduct(catalogsToCarryProduct, [product]).then(() => {
                            setNeedsRefresh(true);
                        });
                    }}
                >
                    Add To Stores
                </button>
            </div>
        </Modal>
    );
};

export default StoreSelector;
